import {
  buttonOf,
  drawnAsHeld,
  drawSavedWord,
  postWithoutScript,
  revealAccountOffer,
} from "./save-word-control.js";
import { refusedLocation, toggleSavedWord } from "./save-word-route.js";
import { measureSavedWordRequest } from "./save-word-latency.js";
import { showMilestone } from "./save-word-milestone.js";
import type { StandingPanel } from "./study-standing.js";
import { standingPanelFor } from "./study-standing-badge.js";
import type { SaveWordsOptions } from "./save-word-options.js";

/**
 * What one save control does when it is pressed.
 *
 * Apart from `save-word.ts`, which finds the controls and asks the route which
 * words a reader already holds. This is the other half of the same behaviour
 * and the two change for different reasons: which controls exist on a page has
 * moved twice, once when `/user/ci/` grew one per row and again when htmx began
 * replacing those rows, and what a press does has not moved at all.
 */

/** Whether the page says there is a reader to ask the route about. */
export type SessionOnPage = () => boolean;

/**
 * Turn one word over without leaving the page.
 *
 * A reader at the cap is sent to their list, where the offer of Pro 专业 and the
 * words they would have to drop are both drawn. That page is the one place the
 * cap is explained, so the script goes there rather than carrying a second copy
 * of the words.
 *
 * A session that has lapsed since the page was drawn gets the account offer, the
 * same as anybody signed out.
 */
async function press(properties: {
  button: HTMLButtonElement;
  control: HTMLElement;
  fetched: typeof fetch;
  options: SaveWordsOptions;
  panel: StandingPanel | undefined;
}): Promise<void> {
  const word = properties.button.value;
  const tag = properties.options.tag?.();
  // The press asks for what the button offers. A page drawn before the word
  // was saved in another tab still offers to save it (CB-44).
  const intent = drawnAsHeld(properties.button) ? "remove" : "save";
  const result = await measureSavedWordRequest(
    properties.options.requestStarted,
    async () => toggleSavedWord(properties.fetched, word, tag, intent),
  );

  if (result.full) {
    if (properties.options.onFull === undefined) {
      globalThis.location.assign(refusedLocation(word));
    } else {
      properties.options.onFull(word);
    }
  } else if (result.signedOut) {
    revealAccountOffer(properties.control);
  } else {
    drawSavedWord(properties.button, result.held);
    properties.options.onChange?.({ word, held: result.held });

    // A word just saved opens its panel, which is where the reader learns what
    // saving it has set going. A save that reached a milestone says so there,
    // or beside the control where it has no panel (CB-61).
    if (result.held) {
      const { milestone } = result;
      const place = (panel: HTMLElement | undefined): void => {
        if (milestone !== undefined) {
          showMilestone(panel, properties.control, milestone);
        }
      };

      // A panel that cannot be drawn leaves the line beside the control, since
      // the handler has claimed the milestone and will not offer it again.
      void (properties.panel?.open() ?? Promise.resolve(false)).then(
        (shown) => {
          place(shown ? properties.panel?.element : undefined);
        },
        () => {
          place(undefined);
        },
      );
    }
  }
}

/**
 * Take the presses on one control.
 * @param properties - The control, whether the page has a session, and what to
 * ask with.
 * @returns The panel the control opens, where it is a corner badge.
 */
export function listenForSavedWord(properties: {
  control: HTMLElement;
  signedIn: SessionOnPage;
  fetched: typeof fetch;
  options: SaveWordsOptions;
}): StandingPanel | undefined {
  const { control, signedIn, fetched, options } = properties;
  const form = control.querySelector<HTMLFormElement>(formSelector);
  const button = buttonOf(control);

  if (form === null || button === null) {
    return undefined;
  }

  const panel = standingPanelFor({
    control,
    button,
    fetched,
    remove: () => {
      turnOver();
    },
  });
  const turnOver = (): void => {
    void press({ button, control, fetched, options, panel }).catch(() => {
      postWithoutScript(form, button.value, options.tag?.());
    });
  };

  form.addEventListener("submit", (event: Event) => {
    event.preventDefault();

    if (!signedIn()) {
      revealAccountOffer(control);

      return;
    }

    if (options.valid?.({ held: drawnAsHeld(button) }) === false) {
      return;
    }

    // A held badge opens its panel, and turns the word over only where the
    // handler had no panel to draw.
    if (panel !== undefined && drawnAsHeld(button)) {
      void panel
        .toggle()
        .then((shown) => {
          if (!shown) {
            turnOver();
          }
        })
        .catch(() => undefined);

      return;
    }

    turnOver();
  });

  return panel;
}

/** The form inside a control. */
const formSelector = ".cb-save-word-form";
